import { Button } from "@components/shared/Button";
import { AlertTriangle, X } from "lucide-react";
import { useCallback, useEffect } from "react";

interface DeletionImpact {
  campaigns: number;
  exclusiveCandidates: number;
  sharedCandidates: number;
  talentPools: number;
  clientContacts: number;
  notes: number;
  portalUsers: number;
}

interface DeleteClientDialogProps {
  open: boolean;
  clientName: string;
  impact: DeletionImpact | null;
  loading: boolean;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteClientDialog({
  open,
  clientName,
  impact,
  loading,
  deleting,
  onCancel,
  onConfirm,
}: DeleteClientDialogProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deleting) onCancel();
    },
    [onCancel, deleting],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="delete-client-dialog"
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss overlay */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={() => !deleting && onCancel()}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !deleting) onCancel();
        }}
      />
      <div className="relative flex w-full max-w-md flex-col rounded-lg bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-error" />
            <h2 className="text-sm font-semibold text-text-primary">Delete {clientName}</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 px-6 py-4 text-sm text-text-secondary">
          <div className="mb-4 rounded-md border border-error/30 bg-error/5 px-3 py-2 text-xs text-error">
            This action cannot be undone. All data listed below will be permanently deleted.
          </div>

          {loading ? (
            <p className="text-text-muted">Calculating impact...</p>
          ) : impact ? (
            <div className="space-y-2">
              <ImpactRow label="Campaigns" count={impact.campaigns} />
              {impact.exclusiveCandidates > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                  <strong>{impact.exclusiveCandidates} candidate(s)</strong> exist only in this
                  client's campaigns and will be permanently deleted.
                </div>
              )}
              {impact.sharedCandidates > 0 && (
                <ImpactRow
                  label="Shared candidates (unlinked, not deleted)"
                  count={impact.sharedCandidates}
                />
              )}
              <ImpactRow label="Talent pools" count={impact.talentPools} />
              <ImpactRow label="Client contacts" count={impact.clientContacts} />
              <ImpactRow label="Notes" count={impact.notes} />
              <ImpactRow label="Portal users" count={impact.portalUsers} />

              {impact.campaigns > 0 && (
                <p className="mt-3 text-xs text-text-muted">
                  To keep specific campaigns, reassign them to another client before deleting.
                </p>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={deleting}
            data-testid="delete-client-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={loading || deleting}
            data-testid="delete-client-confirm"
          >
            {deleting ? "Deleting..." : "Delete client"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImpactRow({ label, count }: { label: string; count: number }) {
  if (count === 0) return null;
  return (
    <div className="flex items-center justify-between text-xs">
      <span>{label}</span>
      <span className="font-medium text-text-primary">{count}</span>
    </div>
  );
}
